import React from 'react';
import { Country } from '../../../../API/Enums';
import { useScanLicense } from '../../../../context/hardware';
import { useKioskImage, useKioskState } from '../../../../context/Kiosk/kiosk';
import { useLocalization } from '../../../../context/Localization/localization';
import { ScanErrorCode } from '../../../../context/mzero';
import { useHideKioskBackground } from '../../../../context/Page/page';
import { Heading } from '../../../../controls/Heading/Heading';
import { KioskImageDisplay } from '../../../../controls/KioskImageDisplay';
import { ScrollableBody } from '../../../Layout/ScrollableBody/ScrollableBody';
import { Confirm } from './Confirm';
import { ICaptureResults, ILicenseScanIndexProps } from './LicenseScanIndex';
import { GMButton } from '../../../../controls/Button';
import { GMDialog } from '../../../../controls/Dialog';
import { GMButtonGroup } from '../../../../controls/ButtonGroup';
import { GMRoundedIcon, GMSizedIcon } from '../../../../controls/Icon';

export const LicenseScanKioskIndex = (props: ILicenseScanIndexProps) => {
    useHideKioskBackground()();
    const [results, setResults] = React.useState<ICaptureResults>();

    if (results) {
        return (
            <Confirm
                results={results}
                onInformationCorrect={props.onProceed}
                onInformationIncorrect={props.onManualEntry}
            />
        );
    }
    else {
        return <LicenseScanKiosk onScan={setResults} onManualEntry={props.onManualEntry} />;
    }
};

const LicenseScanKiosk = (props: { onScan: (results?: ICaptureResults) => void; onManualEntry: () => void; }) => {
    const Localization = useLocalization();
    const { result, rawResult, start, error, reset, simulateError, simulateResult } = useScanLicense();
    const licenseScanImage = useKioskImage('LicenseScan');
    const scanBarcodeImage = useKioskImage('LicenseScanBack');
    const {
        dealer: { country },
    } = useKioskState();

    React.useEffect(() => {
        if (!error && !result && !rawResult) {
            start();
        }
    }, [start, error, result, rawResult]);

    const { onScan } = props;

    const tryAgain = () => {
        reset();
        start();
    };

    React.useEffect(() => {
        if (result) {
            const customerDict = {
                firstName: result.FirstName,
                lastName: result.LastName,
                street: result.StreetAddress,
                city: result.City,
                stateOrProvince: result.StateName,
                postalCode: country === Country.Canada ? result.PostalCode : result.PostalCode.slice(0, 5),
            } as ICaptureResults;
            onScan(customerDict);
        }
    }, [result, onScan, country]);

    return (
        <ScrollableBody
            heading={
                <Heading
                    primaryHeading={Localization.ScanYourLicense}
                    secondaryHeading={Localization.PleaseScanYourLicense}
                    hint={Localization.PleasePositionYourLicense}
                    roundedIcon={
                        <GMRoundedIcon themeType={'secondary'} roundedBackgroundSize={'large'}>
                            <GMSizedIcon
                                iconType={'License'}
                                iconSize={'extraLarge'}
                                ariaLabel={Localization.DriversLicenseIcon}
                            />
                        </GMRoundedIcon>
                    }
                    showMustache
                />
            }
        >
            <KioskImageDisplay
                left={{ image: licenseScanImage,
                    alt: Localization.DriversLicenseIcon }}
                right={{
                    top: {
                        text: Localization.ScanLicenseUsingTheHighlightedReader,
                    },
                    image: scanBarcodeImage,
                    bottom: Localization.PositionTheLicenseBarcode,
                }}
            />
            <GMDialog
                ariaLabel={Localization.AnErrorHasOccured}
                open={!!error}
                onClose={props.onManualEntry}
                title={Localization.AnErrorHasOccured}
                actions={[
                    <GMButton
                        id={Localization.TryAgain}
                        title={Localization.TryAgain}
                        ariaLabel={Localization.TryAgain}
                        onClick={tryAgain}
                    >
                        {Localization.TryAgain}
                    </GMButton>,
                    <GMButton
                        id={Localization.ClickToBeginManualInput}
                        title={Localization.ClickToBeginManualInput}
                        ariaLabel={Localization.ClickToBeginManualInput}
                        onClick={props.onManualEntry}
                    >
                        {Localization.ManualEntry}
                    </GMButton>,
                ]}
            >
                {Localization.UnableToRead}
            </GMDialog>
            {simulateError && simulateResult && (
                <GMButtonGroup
                    buttons={[
                        (
                            <GMButton
                                id={'Timeout'}
                                title={'Timeout'}
                                ariaLabel={'Timeout'}
                                onClick={() => simulateError(ScanErrorCode.timeout)}
                            >
                                {'Timeout\r'}
                            </GMButton>
                        ),
                        (
                            <GMButton
                                id={'In Use'}
                                title={'In Use'}
                                ariaLabel={'In Use'}
                                onClick={() => simulateError(ScanErrorCode.inUse)}
                            >
                                {'In Use\r'}
                            </GMButton>
                        ),
                        (
                            <GMButton
                                id={'Bad Data'}
                                title={'Bad Data'}
                                ariaLabel={'Bad Data'}
                                onClick={() => simulateError(ScanErrorCode.badData)}
                            >
                                {'Bad Data\r'}
                            </GMButton>
                        ),
                        (
                            <GMButton
                                id={'Simulate Result'}
                                title={'Simulate Result'}
                                ariaLabel={'Simulate Result'}
                                onClick={simulateResult}
                            >
                                {'Simulate Result\r'}
                            </GMButton>
                        ),
                    ]}
                />
            )}
        </ScrollableBody>
    );
};
